import { useState } from "react";

import { KeyRound, Loader2, ShieldOff, Trash2, UserCheck, UserPlus } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AssignRolesDialog } from "@/features/rbac/components/AssignRolesDialog";
import { Can } from "@/features/rbac/components/Can";
import { usePermissions } from "@/features/rbac/hooks/usePermissions";
import { StaffFormDialog } from "@/features/staff/components/StaffFormDialog";
import { StaffMigrationPanel } from "@/features/staff/components/StaffMigrationPanel";
import { StaffPasswordDialog } from "@/features/staff/components/StaffPasswordDialog";
import type { StaffMember } from "@/features/staff/schemas/staff";
import {
  useDeleteStaffMember,
  useStaffMembers,
  useUpdateStaffMember,
} from "@/features/staff/hooks/useStaff";

/**
 * Equipo interno con acceso al panel.
 *
 * Estas cuentas viven en `staff`, separadas de los usuarios de la plataforma
 * médica. Una misma persona puede tener su cuenta de paciente y, aparte, la
 * corporativa del equipo: son dos identidades distintas de Firebase Auth.
 */
export function StaffEditor() {
  const { data: members = [], isLoading } = useStaffMembers();
  const updateMember = useUpdateStaffMember();
  const deleteMember = useDeleteStaffMember();
  const { subject } = usePermissions();

  const [rolesFor, setRolesFor] = useState<StaffMember | null>(null);
  const [passwordFor, setPasswordFor] = useState<StaffMember | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  /** Recién creado: se abre su diálogo de roles para darle acceso enseguida. */
  const [pendingRoles, setPendingRoles] = useState<{ uid: string; email: string } | null>(
    null
  );

  const handleToggleActive = async (member: StaffMember) => {
    try {
      await updateMember.mutateAsync({
        uid: member.uid,
        data: { active: !member.active },
      });
      toast.success(
        member.active
          ? `${member.email} ya no puede entrar al panel`
          : `${member.email} vuelve a tener acceso`
      );
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "No se pudo actualizar el acceso"
      );
    }
  };

  const handleDelete = async (member: StaffMember) => {
    try {
      await deleteMember.mutateAsync(member.uid);
      toast.success(`${member.email} eliminado del equipo`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "No se pudo eliminar del equipo"
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold">Equipo</h2>
          <p className="text-sm text-muted-foreground">
            Cuentas corporativas con acceso al panel. Están separadas de los usuarios de la
            plataforma médica: una misma persona puede tener su cuenta de paciente y,
            aparte, la del equipo.
          </p>
        </div>

        <Can permission="roles:assign">
          <Button size="sm" className="shrink-0" onClick={() => setShowCreate(true)}>
            <UserPlus className="mr-2 h-4 w-4" />
            Nuevo miembro
          </Button>
        </Can>
      </div>

      <Can permission="roles:assign">
        <StaffMigrationPanel />
      </Can>

      {members.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Todavía no hay cuentas de equipo. Migra las que hoy entran con el acceso
            antiguo, o crea la primera con «Nuevo miembro»: recibirá el rol Administrador
            automáticamente, porque en cuanto exista un miembro el acceso antiguo se cierra.
          </CardContent>
        </Card>
      ) : (
        <ul className="space-y-2">
          {members.map((member) => {
            const isSelf = member.uid === subject?.uid;

            return (
              <li
                key={member.uid}
                className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-medium">{member.name || member.email}</p>
                    {!member.active && <Badge variant="destructive">Sin acceso</Badge>}
                    {isSelf && <Badge variant="outline">Tú</Badge>}
                  </div>
                  <code className="text-xs text-muted-foreground">{member.email}</code>
                  {member.position && (
                    <p className="text-sm text-muted-foreground">{member.position}</p>
                  )}
                </div>

                <div className="flex shrink-0 gap-1">
                  <Can permission="roles:assign">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRolesFor(member)}
                      disabled={isSelf}
                      title={
                        isSelf
                          ? "No puedes modificar tus propios roles"
                          : "Gestionar roles"
                      }
                    >
                      Roles
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPasswordFor(member)}
                      title="Regenerar contraseña"
                    >
                      <KeyRound className="h-4 w-4" />
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void handleToggleActive(member)}
                      disabled={isSelf || updateMember.isPending}
                      title={
                        isSelf
                          ? "No puedes revocarte el acceso a ti mismo"
                          : member.active
                            ? "Revocar acceso"
                            : "Restaurar acceso"
                      }
                    >
                      {member.active ? (
                        <ShieldOff className="h-4 w-4" />
                      ) : (
                        <UserCheck className="h-4 w-4" />
                      )}
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void handleDelete(member)}
                      disabled={isSelf || deleteMember.isPending}
                      title={
                        isSelf
                          ? "No puedes eliminarte del equipo"
                          : "Eliminar del equipo"
                      }
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </Can>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <StaffFormDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(uid, email) => setPendingRoles({ uid, email })}
      />

      <StaffPasswordDialog
        member={passwordFor}
        open={passwordFor !== null}
        onClose={() => setPasswordFor(null)}
      />

      <AssignRolesDialog
        subject={
          pendingRoles
            ? {
                uid: pendingRoles.uid,
                email: pendingRoles.email,
                displayName: pendingRoles.email,
                subjectType: "staff",
              }
            : null
        }
        open={pendingRoles !== null}
        onClose={() => setPendingRoles(null)}
      />

      <AssignRolesDialog
        subject={
          rolesFor
            ? {
                uid: rolesFor.uid,
                email: rolesFor.email,
                displayName: rolesFor.name || rolesFor.email,
                subjectType: "staff",
              }
            : null
        }
        open={rolesFor !== null}
        onClose={() => setRolesFor(null)}
      />
    </div>
  );
}
